import React, { useEffect, useState } from "react";
import {
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiDescriptionList,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiHealth,
  EuiLink,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Contact, LeadPriority, LeadStatus } from "..";
import { useAnketaI18n } from "src/internationalization";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { CopyButton } from "src/questionnaires/components/question_card";

const priorityColor = (priority: LeadPriority): string => {
  switch (priority) {
    case LeadPriority.high:
      return "danger";
    case LeadPriority.medium:
      return "warning";
    default:
      return "default";
  }
};

const statusColor = (status: LeadStatus): string => {
  switch (status) {
    case LeadStatus.new:
      return "primary";
    case LeadStatus.in_progress:
      return "accent";
    case LeadStatus.resolved:
      return "success";
    default:
      return "subdued";
  }
};

const formatDate = (value: string): string => {
  const date = moment(new Date(value));
  return date.isValid()
    ? `${date.format("DD.MM.YYYY HH:mm")} (${date.fromNow()})`
    : "—";
};

/**
 * Read-only details of a contact request. The flyout used to render only a
 * "Summary" tab with an empty body, so the message itself - the one thing a
 * request is about - was never visible anywhere except the edit form.
 */
export const ContactFlyout = ({
  onClose,
  onEdit,
  selectedContact,
}: {
  onClose: () => void;
  onEdit?: (contact: Contact) => void;
  selectedContact: Contact;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const navigate = useNavigate();

  // Resolve the linked questionnaire (the contact stores only its id; the
  // route also needs the catalog id).
  const [questionnaire, setQuestionnaire] = useState<{
    name: string;
    path: string;
  } | null>(null);
  useEffect(() => {
    setQuestionnaire(null);
    if (!selectedContact.questionnaireId || !keycloak?.token) {
      return;
    }
    let cancelled = false;
    Get<{ questioners: Array<any>; catalog: { id: string } }>({
      path: `v2/${AnketaResourcesEnum.opsanio}/questioners`,
      responseType: "json",
      token: keycloak.token,
    }).then((resp) => {
      const data = resp.data as any;
      if (cancelled || !resp.success || !data?.catalog) return;
      const match = (data.questioners ?? []).find(
        (q: any) => q?.id === selectedContact.questionnaireId,
      );
      if (match) {
        setQuestionnaire({
          name: match.displayName ?? match.id,
          path: `/${AnketaResourcesEnum.questionnaires}/${data.catalog.id}/${match.id}`,
        });
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact.questionnaireId]);

  const priorityLabel = translate(
    `anketa.leads.priority.${selectedContact.priority}`,
    { defaultMessage: selectedContact.priority },
  );
  const statusLabel = translate(
    `anketa.leads.status.${selectedContact.status}`,
    { defaultMessage: selectedContact.status },
  );

  const replyHref = `mailto:${encodeURIComponent(selectedContact.email)}?subject=${encodeURIComponent(
    `Re: ${selectedContact.topic ?? ""}`,
  )}`;

  const details = [
    {
      title: translate("anketa.opsanio.contact.field.email", {
        defaultMessage: "E-mail",
      }),
      description: (
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiLink href={`mailto:${selectedContact.email}`}>
              {selectedContact.email}
            </EuiLink>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <CopyButton value={selectedContact.email} />
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
    {
      title: translate("anketa.opsanio.contact.field.company", {
        defaultMessage: "Company",
      }),
      description: selectedContact.company || "—",
    },
    {
      title: translate("anketa.opsanio.contact.field.topic", {
        defaultMessage: "Topic",
      }),
      description: selectedContact.topic || "—",
    },
    {
      title: translate("anketa.opsanio.contact.detail.questionnaire", {
        defaultMessage: "Questionnaire",
      }),
      description: questionnaire ? (
        <EuiLink onClick={() => navigate(questionnaire.path)}>
          {questionnaire.name}
        </EuiLink>
      ) : selectedContact.questionnaireId ? (
        <span className="aqReadonlyValue">{selectedContact.questionnaireId}</span>
      ) : (
        translate("anketa.opsanio.contact.detail.noQuestionnaire", {
          defaultMessage: "Not linked",
        })
      ),
    },
    {
      title: translate("anketa.opsanio.contact.detail.created", {
        defaultMessage: "Received",
      }),
      description: formatDate(selectedContact.createdAt),
    },
    {
      title: translate("anketa.opsanio.contact.detail.updated", {
        defaultMessage: "Last updated",
      }),
      description: formatDate(selectedContact.updatedAt),
    },
  ];

  return (
    <EuiFlyout onClose={onClose} size="m" aria-labelledby="contactFlyoutTitle">
      <EuiFlyoutHeader hasBorder>
        <EuiTitle size="m">
          <h2 id="contactFlyoutTitle">{selectedContact.name}</h2>
        </EuiTitle>
        {selectedContact.company && (
          <EuiText size="s" color="subdued">
            <p>{selectedContact.company}</p>
          </EuiText>
        )}
        <EuiSpacer size="s" />
        <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiHealth color={statusColor(selectedContact.status)}>
              {statusLabel}
            </EuiHealth>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiBadge color={priorityColor(selectedContact.priority)}>
              {translate("anketa.leads.priority", {
                defaultMessage: "Priority",
              })}
              : {priorityLabel}
            </EuiBadge>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiTitle size="xs">
          <h3>
            {translate("anketa.opsanio.contact.field.message", {
              defaultMessage: "Message",
            })}
          </h3>
        </EuiTitle>
        <EuiSpacer size="s" />
        <EuiPanel color="subdued" hasShadow={false} paddingSize="m">
          <EuiText size="s">
            {/* Keep the sender's line breaks. */}
            <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {selectedContact.message || "—"}
            </p>
          </EuiText>
        </EuiPanel>

        <EuiSpacer size="l" />
        <EuiDescriptionList
          type="column"
          compressed
          listItems={details}
          columnWidths={[1, 2]}
        />
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent="spaceBetween" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty iconType="cross" onClick={onClose} flush="left">
              {translate("anketa.common.close", { defaultMessage: "Close" })}
            </EuiButtonEmpty>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiFlexGroup gutterSize="s" responsive={false}>
              {onEdit && (
                <EuiFlexItem grow={false}>
                  <EuiButton
                    iconType="pencil"
                    onClick={() => onEdit(selectedContact)}
                  >
                    {translate("anketa.opsanio.contact.detail.edit", {
                      defaultMessage: "Edit",
                    })}
                  </EuiButton>
                </EuiFlexItem>
              )}
              <EuiFlexItem grow={false}>
                <EuiButton fill iconType="email" href={replyHref}>
                  {translate("anketa.opsanio.contact.detail.reply", {
                    defaultMessage: "Reply by e-mail",
                  })}
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  );
};
